"use client"

import { useState } from "react"
import Image from "next/image"
import Link from "next/link"
import { motion } from "framer-motion"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Play, Heart, Share2, ShoppingCart } from "lucide-react"
import ProductVideo from "../video/product-video"

interface Product {
  id: number
  name: string
  category: string | null
  subcategory: string | null
  image: string
  alt: string
  meta: {
    title: string
    description: string
    keywords: string
  }
}

interface MobileProductCardProps {
  product: Product
  index?: number
}

export default function MobileProductCard({ product, index = 0 }: MobileProductCardProps) {
  const [showVideo, setShowVideo] = useState(false)
  const [isLiked, setIsLiked] = useState(false)

  if (!product || !product.meta) {
    return null
  }

  const slug = product.name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")

  const handleShare = async () => {
    const shareData = {
      title: product.name,
      text: `${product.name}\n\n${product.meta.description}`,
      url: `${window.location.origin}/products/${slug}-${product.id}`,
    }

    if (navigator.share) {
      try {
        await navigator.share(shareData)
        console.log("Product shared successfully")
      } catch (err) {
        console.log("Error sharing:", err)
      }
    } else {
      // Fallback: copy to clipboard
      try {
        await navigator.clipboard.writeText(`${shareData.text}\n${shareData.url}`)
        alert("Product details copied to clipboard!")
      } catch (err) {
        console.log("Error copying to clipboard:", err)
      }
    }
  }


  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      className="w-full"
    >
      <Card className="overflow-hidden bg-white shadow-lg border-0 rounded-2xl">
        <CardContent className="p-0">
          {/* Media Section */}
          <div className="relative aspect-square">
            {showVideo ? (
              <ProductVideo productType={product.category ?? ""} />
            ) : (
              <div className="relative w-full h-full">
                <Image
                  src={`${product.image}`}
                  alt={product.alt}
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 100vw, 50vw"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
              </div>
            )}

            {/* Media Controls */}
            <div className="absolute top-3 left-3 right-3 flex justify-between items-start">
              <Badge className="bg-black/50 text-white border-0 backdrop-blur-sm">{product.category}</Badge>
              <div className="flex space-x-2">
                {/* <motion.button
                  className="p-2 bg-black/50 backdrop-blur-sm rounded-full text-white"
                  onClick={() => setShowVideo(!showVideo)}
                  whileTap={{ scale: 0.9 }}
                >
                  <Play className="h-4 w-4" />
                </motion.button>
                <motion.button
                  className="p-2 bg-black/50 backdrop-blur-sm rounded-full text-white"
                  onClick={() => setIsLiked(!isLiked)}
                  whileTap={{ scale: 0.9 }}
                >
                  <Heart className={`h-4 w-4 ${isLiked ? "fill-red-500 text-red-500" : ""}`} />
                </motion.button> */}
              </div>
            </div>

            {/* Quick Action Button */}
            <div className="absolute bottom-3 right-3">
              <motion.div whileTap={{ scale: 0.9 }}>
                <Button
                  size="sm"
                  className="bg-white text-slate-900 hover:bg-slate-100 shadow-lg rounded-full px-4"
                  asChild
                >
                  <Link href={`/products/${slug}-${product.id}`} aria-label={`View details for ${product.name}`}>
                    <ShoppingCart className="h-4 w-4 mr-2" />
                    View
                  </Link>
                </Button>
              </motion.div>
            </div>
          </div>

          {/* Content Section */}
          <div className="p-4">
            <div className="flex items-start justify-between mb-2">
              <h3 className="font-semibold text-lg line-clamp-2 flex-1 mr-2">{product.name}</h3>
              <motion.button className="p-1 text-slate-400 hover:text-slate-600" whileTap={{ scale: 0.9 }}>
                <Share2 className="h-4 w-4"
                  onClick={handleShare}
                  aria-label={`Share ${product.name}`}
                />
              </motion.button>
            </div>

            <p className="text-slate-600 text-sm line-clamp-2 mb-3 leading-relaxed">
              {product.meta?.description || "No description available"}
            </p>

            <div className="flex items-center justify-between">
              {product.subcategory && (
                <Badge variant="outline" className="text-xs">
                  {product.subcategory}
                </Badge>
              )}
              <div className="flex items-center space-x-1 text-xs text-slate-500">
                <span>•</span>
                <span>In Stock</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  )
}
